import React from "react";
import { IDatabaseLineItem } from "../../LineItemDBCreationToolPage";
import { LineItemOrderObject } from "../../../types";
import { CatGroup, DragState } from "./CatOrderTable";
import CatLineItemRow from "./CatLineItemRow";

export interface CatGroupRowProps {
	group: CatGroup;
	groupStartIndex: number;
	isDraggingGroup: boolean;
	isDraggingItem: boolean;
	dragState: DragState;
	selectedIds: Set<string>;
	getLineItemById: (id: string) => IDatabaseLineItem | undefined;
	onGroupDragStart: (e: React.DragEvent, cat: string) => void;
	onGroupDragOver: (e: React.DragEvent) => void;
	onGroupDrop: (e: React.DragEvent, cat: string) => void;
	onItemDropOnHeader: (e: React.DragEvent, cat: string) => void;
	onDragEnd: () => void;
	onItemDragStart: (e: React.DragEvent, item: LineItemOrderObject, cat: string) => void;
	onItemDragOver: (e: React.DragEvent) => void;
	onItemDrop: (e: React.DragEvent, item: LineItemOrderObject, cat: string) => void;
	onItemClick: (e: React.MouseEvent, item: LineItemOrderObject) => void;
}

const CatGroupRow: React.FC<CatGroupRowProps> = ({
	group,
	groupStartIndex,
	isDraggingGroup,
	isDraggingItem,
	dragState,
	selectedIds,
	getLineItemById,
	onGroupDragStart,
	onGroupDragOver,
	onGroupDrop,
	onItemDropOnHeader,
	onDragEnd,
	onItemDragStart,
	onItemDragOver,
	onItemDrop,
	onItemClick,
}) => {
	const isGroupBeingDragged =
		isDraggingGroup &&
		dragState?.type === "group" &&
		(dragState as { type: "group"; draggedCat: string }).draggedCat === group.cat;

	return (
		<React.Fragment>
			{/* CAT group header — draggable to reorder entire group */}
			<tr
				draggable
				onDragStart={(e) => onGroupDragStart(e, group.cat)}
				onDragOver={onGroupDragOver}
				onDrop={(e) => {
					if (isDraggingItem) {
						onItemDropOnHeader(e, group.cat);
					} else {
						onGroupDrop(e, group.cat);
					}
				}}
				onDragEnd={onDragEnd}
				className={`bg-indigo-50 cursor-move select-none transition-opacity ${isGroupBeingDragged ? "opacity-50" : ""}`}
			>
				<td colSpan={2} className="px-6 py-2">
					<div className="flex items-center gap-2">
						<svg className="w-4 h-4 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
						</svg>
						<span className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">
							{group.cat}
						</span>
						<span className="text-xs text-indigo-400">({group.items.length} items)</span>
					</div>
				</td>
			</tr>

			{/* Line items within the group */}
			{group.items.map((orderItem, itemIndexInGroup) => {
				const lineItem = getLineItemById(orderItem.lineItemId);
				const displayIndex = groupStartIndex + itemIndexInGroup;
				const isBeingDragged =
					isDraggingItem &&
					dragState?.type === "item" &&
					(dragState as { type: "item"; draggedItems: LineItemOrderObject[] }).draggedItems.some(
						(d) => d.lineItemId === orderItem.lineItemId,
					);

				return (
					<CatLineItemRow
						key={orderItem.lineItemId}
						orderItem={orderItem}
						displayIndex={displayIndex}
						groupCat={group.cat}
						lineItem={lineItem}
						isBeingDragged={isBeingDragged}
						isSelected={selectedIds.has(orderItem.lineItemId)}
						onDragStart={onItemDragStart}
						onDragOver={onItemDragOver}
						onDrop={onItemDrop}
						onDragEnd={onDragEnd}
						onClick={onItemClick}
					/>
				);
			})}
		</React.Fragment>
	);
};

export default CatGroupRow;
